import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, Tv, Radio, Film, Check, Zap, Wifi, Smartphone, Monitor, Building2, Users, Calendar, ArrowRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const ConoceBravoPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const stats = [
    { number: '125+', label: 'Cines instalados', icon: Building2 },
    { number: '250.000+', label: 'Espectadores', icon: Users },
    { number: '35+', label: 'Eventos en directo', icon: Radio },
  ];

  const contentTypes = [
    {
      icon: Radio,
      title: 'Directos',
      description: 'Retransmisiones en vivo de óperas, ballet y conciertos desde los mejores teatros del mundo',
      color: '#e4002b'
    },
    {
      icon: Play,
      title: 'Encores',
      description: 'Repeticiones de los mejores eventos para que tu público no se pierda nada',
      color: '#ff6b35'
    },
    {
      icon: Film,
      title: 'Grabados',
      description: 'Contenido pregrabado de alta calidad para proyectar cuando lo necesites',
      color: '#4ecdc4'
    }
  ];

  const benefits = [
    { icon: Zap, title: 'Instalación ágil', description: 'Sin satélite, sin complicaciones. Solo necesitas conexión a internet.' },
    { icon: Wifi, title: 'Streaming privado', description: 'VPN dedicada para máxima calidad y seguridad en cada proyección.' },
    { icon: Smartphone, title: 'Control total', description: 'Gestiona todo desde tu móvil, tablet u ordenador.' },
    { icon: Monitor, title: 'Alta definición', description: 'Video 1080p a 50Hz y audio 5.1 multicanal.' },
  ];

  const partners = [
    { name: 'Royal Opera House', logo: 'https://upload.wikimedia.org/wikipedia/en/thumb/e/ed/Royal_Opera_House_logo.svg/200px-Royal_Opera_House_logo.svg.png' },
    { name: 'Teatro Real', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/Teatro_Real_de_Madrid.svg/200px-Teatro_Real_de_Madrid.svg.png' },
    { name: 'Berliner Philharmoniker', logo: 'https://upload.wikimedia.org/wikipedia/commons/thumb/8/8c/Berliner_Philharmoniker_Logo_2012.svg/200px-Berliner_Philharmoniker_Logo_2012.svg.png' },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header onOpenAuth={() => setShowAuth(true)} onOpenSearch={() => setShowSearch(true)} />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1690131054032-1d7465c6b662?w=1920&q=80" 
            alt="Teatro"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-black/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-1 bg-[#e4002b]" />
              <span className="text-[#e4002b] text-sm font-semibold tracking-widest uppercase">Streaming para cines</span>
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
              La cultura en
              <span className="text-[#e4002b]"> tu cine</span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-8 leading-relaxed">
              Directos, encores y contenidos grabados de los mejores teatros del mundo.
              <span className="text-white font-semibold"> Tan sencillo como darle al play.</span>
            </p>

            <div className="flex flex-wrap gap-4">
              <Link 
                to="/cinema/register" 
                className="px-8 py-4 bg-[#e4002b] text-white font-semibold rounded-lg hover:bg-[#c70024] transition-all flex items-center gap-2"
              >
                Empieza ahora <ArrowRight className="w-5 h-5" />
              </Link>
              <a 
                href="#como-funciona" 
                className="px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-all"
              >
                Cómo funciona
              </a>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/50 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#0a0a0a] border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, i) => (
              <div key={i} className="text-center group">
                <stat.icon className="w-10 h-10 text-[#e4002b] mx-auto mb-4 group-hover:scale-110 transition-transform" />
                <p className="text-5xl font-bold text-white mb-2">{stat.number}</p>
                <p className="text-white/60 uppercase tracking-wider text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Content Types Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Tres formas de llevar la <span className="text-[#e4002b]">cultura</span> a tu público
            </h2>
            <p className="text-white/60 text-lg max-w-2xl mx-auto">
              Con Bravo tienes acceso a contenido exclusivo de los mejores teatros del mundo
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contentTypes.map((type, i) => (
              <div 
                key={i} 
                className="group relative bg-[#141414] rounded-2xl p-8 hover:bg-[#1a1a1a] transition-all overflow-hidden"
              >
                <div 
                  className="absolute top-0 left-0 w-full h-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  style={{ backgroundColor: type.color }}
                />
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                  style={{ backgroundColor: `${type.color}20` }}
                >
                  <type.icon className="w-8 h-8" style={{ color: type.color }} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">{type.title}</h3>
                <p className="text-white/60 leading-relaxed">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works Section */}
      <section id="como-funciona" className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1673437791527-41b97b1c1a37?w=800&q=80" 
                alt="Cinema interior"
                className="rounded-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-[#e4002b] rounded-xl p-6 shadow-2xl">
                <p className="text-white font-bold text-lg">Solo necesitas</p>
                <p className="text-white text-3xl font-bold">22 Mbps</p>
                <p className="text-white/80 text-sm">de conexión</p>
              </div>
            </div>

            {/* Content */}
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Así de <span className="text-[#e4002b]">sencillo</span>
              </h2>
              <p className="text-white/60 text-lg mb-8">
                Sin satélite, sin complicaciones técnicas. Bravo funciona mediante streaming 
                a través de una VPN privada que garantiza la máxima calidad y seguridad.
              </p>

              <div className="space-y-6">
                {[
                  { step: '1', title: 'Conecta el reproductor', desc: 'Enchufa el dispositivo Bravo a tu proyector y al router' },
                  { step: '2', title: 'Descarga la app', desc: 'Disponible para móvil, tablet y ordenador' },
                  { step: '3', title: 'Dale al play', desc: 'Controla la reproducción, pausa, volumen y resolución' },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 items-start group">
                    <div className="w-12 h-12 rounded-full bg-[#e4002b]/20 flex items-center justify-center flex-shrink-0 group-hover:bg-[#e4002b] transition-colors">
                      <span className="text-[#e4002b] font-bold text-lg group-hover:text-white transition-colors">{item.step}</span>
                    </div>
                    <div>
                      <h4 className="text-white font-semibold text-lg mb-1">{item.title}</h4>
                      <p className="text-white/60">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Tecnología <span className="text-[#e4002b]">sin complicaciones</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, i) => (
              <div 
                key={i} 
                className="bg-[#141414] rounded-xl p-6 hover:bg-[#1a1a1a] transition-all group"
              >
                <div className="w-12 h-12 rounded-lg bg-[#e4002b]/20 flex items-center justify-center mb-4 group-hover:bg-[#e4002b]/30 transition-colors">
                  <benefit.icon className="w-6 h-6 text-[#e4002b]" />
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{benefit.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-24 bg-[#141414]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              El mejor contenido <span className="text-[#e4002b]">cultural</span>
            </h2>
            <p className="text-white/60 text-lg">
              Ópera, ballet, conciertos y teatro desde Royal Opera House, Teatro Real y más
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="col-span-2 row-span-2">
              <img 
                src="https://images.unsplash.com/photo-1758670331763-b0f3cd7c1a8d?w=800&q=80" 
                alt="Ballet performance"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1507838153414-b4b713384a76?w=400&q=80" 
                alt="Orchestra"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1514320291840-2e0a9bf2a9ae?w=400&q=80" 
                alt="Concert"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1760542940745-a4b24d5e9b5b?w=400&q=80" 
                alt="Ballet"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
            <div className="aspect-square">
              <img 
                src="https://images.unsplash.com/photo-1769981420455-5e87a0407d6c?w=400&q=80" 
                alt="Opera house"
                className="w-full h-full object-cover rounded-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-[#e4002b]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            ¿Listo para llevar la cultura a tu cine?
          </h2>
          <p className="text-white/90 text-xl mb-10">
            Únete a más de 125 cines que ya están proyectando los mejores espectáculos del mundo
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link 
              to="/cinema/register" 
              className="px-10 py-4 bg-white text-[#e4002b] font-bold rounded-lg hover:bg-white/90 transition-all text-lg"
            >
              Registrar mi cine
            </Link>
            <Link 
              to="/cinema/login" 
              className="px-10 py-4 border-2 border-white text-white font-bold rounded-lg hover:bg-white/10 transition-all text-lg"
            >
              Ya tengo cuenta
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
      {showSearch && <SearchModal onClose={() => setShowSearch(false)} />}
    </div>
  );
};

export default ConoceBravoPage;
